import { useState } from "react";

import { Button2 } from "@/ui2/atoms/Button";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { isAuthorized } from "@/utils/authToken";
import { provider_case_manager, provider_peer } from "@/config/roles";

import AcceptedAgeRangeModal from "./AcceptedAgeRangeModal";

type AgeRange = {
  max: number | string;
  min: number | string;
};

type AcceptedAgeRangeProps = {
  acceptedAgeRanges: AgeRange[];
  noBoxShadow?: boolean;
  setAcceptedAgeRanges: (ranges: AgeRange[]) => void;
};

export function AcceptedAgeRange({
  acceptedAgeRanges,
  noBoxShadow,
  setAcceptedAgeRanges,
}: AcceptedAgeRangeProps) {
  const [showAcceptedAgeRangeModal, setShowAcceptedAgeRangeModal] = useState(false);

  const canEdit = !isAuthorized(provider_case_manager) && !isAuthorized(provider_peer);

  return (
    <>
      {showAcceptedAgeRangeModal && (
        <AcceptedAgeRangeModal
          acceptedAgeRanges={acceptedAgeRanges}
          closeModal={() => setShowAcceptedAgeRangeModal(false)}
          setAcceptedAgeRanges={setAcceptedAgeRanges}
        />
      )}

      <DashboardCard2
        Actions={
          canEdit && (
            <Button2
              onPress={() => setShowAcceptedAgeRangeModal(true)}
              size="sm"
              variant="link"
              {...getAutomationDataAttr("AcceptedAgeRangeEditButton")}
            >
              Edit
            </Button2>
          )
        }
        className={noBoxShadow ? "tw:shadow-none" : undefined}
        title="Accepted Age Range"
      >
        <div className="tw:flex tw:flex-wrap tw:gap-4" data-public>
          {acceptedAgeRanges.map((acceptedAgeRange, index) => (
            <AgeRangeDisplay key={index} max={acceptedAgeRange.max} min={acceptedAgeRange.min} />
          ))}
        </div>
      </DashboardCard2>
    </>
  );
}

type AgeRangeDisplayProps = {
  max: number | string;
  min: number | string;
};

function AgeRangeDisplay({ max, min }: AgeRangeDisplayProps) {
  return (
    <div className="tw:flex tw:gap-4">
      <div className="tw:flex tw:flex-col">
        <span className="tw:text-sm tw:font-bold">Lowest Age</span>
        <span {...getAutomationDataAttr("AcceptedAgeRangeMin")}>{min}</span>
      </div>

      <div className="tw:flex tw:flex-col">
        <span className="tw:text-sm tw:font-bold">Highest Age</span>
        <span {...getAutomationDataAttr("AcceptedAgeRangeMax")}>{max}</span>
      </div>

      <div className="tw:border-r tw:border-stroke-quaternary" />
    </div>
  );
}
